import { DateRangePicker } from 'components/composite/DatePicker'
import Column from 'components/core/Column'
import Row from 'components/core/Row'
import { AddressSearchInput } from 'components/smart/AddressSearchInput'
import { User } from 'types/user.types'
import {
    EXTRA_FIELDS_OPTIONS,
    SIMPLE_EXTRA_FIELDS,
} from '../confirmShipmentDetailsWidget.constants'
import { EquipmentSelect } from 'components/composite/EquipmentSelect'
import styled from '@emotion/styled'
import { useLayoutEffect, useRef, useState } from 'react'
import TextArea from 'components/core/TextArea'
import NumberInput from 'components/composite/NumberInput'
import TextInput from 'components/core/TextInput'
import { pxToRem } from 'utils/rem.utils'
import { ConfirmShipmentDetailsWidgetEditableFieldWrapper } from './ConfirmShipmentDetailsWidgetEditableFieldWrapper'
import { ConfirmShipmentDetailsWidgetCommodity } from './ConfirmShipmentDetailsWidgetCommodity'
import Typography from 'components/core/Typography'
import Button from 'components/core/Button'
import { ExpandArrow } from 'components/core/ExpandArrow'
import { Clock3, Plus, ShieldAlert } from 'lucide-react'
import { MeasurementsTypeSelect } from 'components/composite/MeasurementsTypeSelect'
import { ServiceTypeSelect } from 'components/composite/ServiceTypeSelect'
import Block from 'components/core/Block'
import { typedArrayIncludes } from 'utils/type.utils'
import { useIsMobile } from 'hooks/useIsMobile'
import { AccessorialSelect } from 'components/composite/AccessorialSelect'
import type { ConfirmShipmentDetailsWidgetData } from 'types/widget.types'
import type {
    ArrayExtraFieldId,
    ConfirmShipmentDetailsWidgetErrors,
    SimpleExtraFieldId,
} from '../confirmShipmentDetailsWidget.types'
import { AnchorTag } from 'components/core/AnchorTag'
import { SPACING } from 'constants/spacing.constants'
import { DESIGN_TOKENS } from 'constants/color.constants'

const MAIN_ADDRESS_INPUT_WIDTH_PCT = 60
const MAIN_DATE_INPUT_WIDTH_PCT = 40

const StyledAdditionalFieldsOptionsToggle = styled(Row)`
    cursor: pointer;
    user-select: none;
    max-width: ${pxToRem(200)};
`

export const ConfirmShipmentDetailsWidgetEditable = ({
    isLTL,
    visibleSimpleFieldsMap,
    arrayFieldsIdsMap,
    mutation,
    me,
    errors,
    onChange,
    onBook,
    onCancel,
    onRemoveSimpleField,
    onRemoveArrayField,
    onAddArrayField,
    onAddFieldOptionClick,
}: {
    isLTL?: boolean
    visibleSimpleFieldsMap: Record<SimpleExtraFieldId, boolean>
    arrayFieldsIdsMap: Record<ArrayExtraFieldId, string[]>
    mutation: ConfirmShipmentDetailsWidgetData
    errors: ConfirmShipmentDetailsWidgetErrors
    me: User
    onBook: () => void
    onChange: (data: ConfirmShipmentDetailsWidgetData) => void
    onCancel: () => void
    onAddFieldOptionClick: (
        fieldId: SimpleExtraFieldId | ArrayExtraFieldId
    ) => void
    onAddArrayField: (params: {
        id: ArrayExtraFieldId
        object: Record<string, unknown>
    }) => void
    onRemoveSimpleField: (fieldId: SimpleExtraFieldId) => void
    onRemoveArrayField: (params: {
        id: ArrayExtraFieldId
        index: number
    }) => void
}) => {
    const [isExtraFieldsOpen, setIsExtraFieldsOpen] = useState(false)
    const [addedFieldsCounter, setAddedFieldsCounter] = useState(0)
    const extraFieldsOptionsRef = useRef<HTMLDivElement>(null)
    const isMobile = useIsMobile()

    const _onAddFieldOptionClick = (
        fieldId: SimpleExtraFieldId | ArrayExtraFieldId
    ) => {
        // A small hack to trigger the layout effect which will keep the scroll position
        // on the options to prevent the form from jumping after adding new UI elements.
        setAddedFieldsCounter((prev) => prev + 1)
        onAddFieldOptionClick(fieldId)
    }

    // Scroll field options into view when toggled.
    useLayoutEffect(() => {
        if (isExtraFieldsOpen) {
            extraFieldsOptionsRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
    }, [isExtraFieldsOpen])

    // Stay on the options when adding new fields.
    useLayoutEffect(() => {
        if (addedFieldsCounter > 0) {
            extraFieldsOptionsRef.current?.scrollIntoView({
                behavior: 'auto',
            })
        }
    }, [addedFieldsCounter])

    // These fields will change layout between mobile and desktop
    const resizableFields = {
        pickupLocation: (
            <AddressSearchInput
                isRequiredLabel
                hasAddressBookSearch
                customerId={me.customerId}
                label="Pickup location"
                placeholder="Search pickup"
                error={errors.pickupLocation}
                addressBookId={mutation.pickupLocation?.addressBookId}
                value={mutation.pickupLocation?.placeId ?? ''}
                onChange={(address) =>
                    onChange({
                        ...mutation,
                        pickupLocation: address,
                    })
                }
            />
        ),
        pickupDate: (
            <DateRangePicker
                isRequiredLabel
                isEditable
                hasTime={false}
                label="Pickup date"
                error={errors.pickupDateTimeStartAt}
                placeholder="Pickup date"
                value={{
                    from: mutation.pickupDateTimeStartAt || undefined,
                    to: mutation.pickupDateTimeEndAt || undefined,
                }}
                onChange={(dateRange) =>
                    onChange({
                        ...mutation,
                        pickupDateTimeStartAt: dateRange?.from,
                        pickupDateTimeEndAt: dateRange?.to,
                    })
                }
            />
        ),
        dropoffLocation: (
            <AddressSearchInput
                isRequiredLabel
                hasAddressBookSearch
                customerId={me.customerId}
                label="Delivery location"
                placeholder="Search delivery"
                error={errors.dropoffLocation}
                addressBookId={mutation.dropoffLocation?.addressBookId}
                value={mutation.dropoffLocation?.placeId ?? ''}
                onChange={(address) =>
                    onChange({
                        ...mutation,
                        dropoffLocation: address,
                    })
                }
            />
        ),
        dropoffDate: (
            <DateRangePicker
                isEditable
                label="Delivery date"
                hasTime={false}
                placeholder="Delivery date"
                value={{
                    from: mutation.dropoffDateTimeStartAt || undefined,
                    to: mutation.dropoffDateTimeEndAt || undefined,
                }}
                onChange={(dateRange) =>
                    onChange({
                        ...mutation,
                        dropoffDateTimeStartAt: dateRange?.from,
                        dropoffDateTimeEndAt: dateRange?.to,
                    })
                }
            />
        ),
        budget: (
            <NumberInput
                label="Budget"
                placeholder="Budget"
                prefix="$ "
                value={mutation.maxRate ?? undefined}
                onChange={(maxRate) => onChange({ ...mutation, maxRate })}
            />
        ),
        serviceType: (
            <ServiceTypeSelect
                label="Service type"
                value={mutation.serviceType ?? 'tl'}
                onChange={(serviceType) =>
                    onChange({ ...mutation, serviceType })
                }
            />
        ),
        numberOfShipments: (
            <ConfirmShipmentDetailsWidgetEditableFieldWrapper
                onRemove={() => onRemoveSimpleField('numberOfShipments')}
            >
                <NumberInput
                    label="Quantity"
                    placeholder="# of loads (max 20)"
                    error={errors.numberOfShipments}
                    value={mutation.numberOfShipments ?? undefined}
                    onChange={(numberOfShipments) =>
                        onChange({
                            ...mutation,
                            numberOfShipments,
                        })
                    }
                />
            </ConfirmShipmentDetailsWidgetEditableFieldWrapper>
        ),
        equipment: (
            <EquipmentSelect
                label="Equipment"
                placeholder="Equipment"
                menuPlacement="top"
                equipment={mutation.equipment ?? []}
                onChange={(equipment) => onChange({ ...mutation, equipment })}
            />
        ),
        accessorials: (
            <AccessorialSelect
                label="Accessorials"
                placeholder="Accessorials"
                menuPlacement="top"
                accessorials={mutation.accessorials ?? []}
                onChange={(accessorials) =>
                    onChange({ ...mutation, accessorials })
                }
            />
        ),
        referenceId: (
            <ConfirmShipmentDetailsWidgetEditableFieldWrapper
                onRemove={() => onRemoveSimpleField('referenceId')}
            >
                <TextInput
                    label="Reference ID"
                    placeholder="(e.g., Purchase Order, etc)"
                    value={mutation.referenceId ?? ''}
                    onChange={(referenceId) => {
                        onChange({
                            ...mutation,
                            referenceId,
                        })
                    }}
                />
            </ConfirmShipmentDetailsWidgetEditableFieldWrapper>
        ),
        isRushedOrder: (
            <Row
                p="p1"
                justifyContent="space-between"
                gap="p1"
                alignItems="center"
                bgColor={DESIGN_TOKENS.background.accent}
                br="sm"
            >
                <Row gap="p1" alignItems="center">
                    <Clock3
                        width={SPACING.p2}
                        height={SPACING.p2}
                        color={DESIGN_TOKENS.text.foreground}
                    />
                    <Typography isBold variant="body2" color="primary">
                        This is a rush order
                    </Typography>
                </Row>
                <AnchorTag
                    underline
                    variant="caption"
                    color="secondary"
                    onClick={(e) => {
                        e.preventDefault()
                        onRemoveSimpleField('isRushedOrder')
                        onChange({
                            ...mutation,
                            isRushedOrder: false,
                        })
                    }}
                >
                    Remove
                </AnchorTag>
            </Row>
        ),
        isApprovalRequired: (
            <Row
                p="p1"
                justifyContent="space-between"
                gap="p1"
                alignItems="center"
                bgColor={DESIGN_TOKENS.background.accent}
                br="sm"
            >
                <Row gap="p1" alignItems="center">
                    <ShieldAlert
                        width={SPACING.p2}
                        height={SPACING.p2}
                        color={DESIGN_TOKENS.text.foreground}
                    />
                    <Typography isBold variant="body2" color="primary">
                        Wilson needs approval to book
                    </Typography>
                </Row>
                <AnchorTag
                    underline
                    variant="caption"
                    color="secondary"
                    onClick={(e) => {
                        e.preventDefault()
                        onRemoveSimpleField('isApprovalRequired')
                        onChange({
                            ...mutation,
                            isApprovalRequired: false,
                        })
                    }}
                >
                    Remove
                </AnchorTag>
            </Row>
        ),
    }

    return (
        <Column
            bgColor={DESIGN_TOKENS.background.secondary}
            gap="p1"
            p="p2"
            br="sm"
        >
            {isMobile ? (
                <>
                    {resizableFields.pickupLocation}
                    {resizableFields.pickupDate}
                </>
            ) : (
                <Row gap="p1">
                    <Block widthPct={MAIN_ADDRESS_INPUT_WIDTH_PCT}>
                        {resizableFields.pickupLocation}
                    </Block>
                    <Block widthPct={MAIN_DATE_INPUT_WIDTH_PCT}>
                        {resizableFields.pickupDate}
                    </Block>
                </Row>
            )}

            {mutation.extraPickupLocations?.map((location, fieldIndex) => {
                const key = arrayFieldsIdsMap.extraPickupLocations[fieldIndex]
                return (
                    <ConfirmShipmentDetailsWidgetEditableFieldWrapper
                        key={key}
                        onRemove={() =>
                            onRemoveArrayField({
                                id: 'extraPickupLocations',
                                index: fieldIndex,
                            })
                        }
                    >
                        <AddressSearchInput
                            hasAddressBookSearch
                            customerId={me.customerId}
                            label={`Pickup location ${fieldIndex + 2}`}
                            placeholder="Search pickup"
                            addressBookId={location.addressBookId}
                            value={location.placeId ?? ''}
                            onChange={(address) =>
                                onChange({
                                    ...mutation,
                                    extraPickupLocations: (
                                        mutation.extraPickupLocations ?? []
                                    ).map((extraPickup, pickupIndex) => {
                                        return pickupIndex === fieldIndex
                                            ? address
                                            : extraPickup
                                    }),
                                })
                            }
                        />
                    </ConfirmShipmentDetailsWidgetEditableFieldWrapper>
                )
            })}

            {isMobile ? (
                <>
                    {resizableFields.dropoffLocation}
                    {resizableFields.dropoffDate}
                </>
            ) : (
                <Row gap="p1">
                    <Block widthPct={MAIN_ADDRESS_INPUT_WIDTH_PCT}>
                        {resizableFields.dropoffLocation}
                    </Block>
                    <Block widthPct={MAIN_DATE_INPUT_WIDTH_PCT}>
                        {resizableFields.dropoffDate}
                    </Block>
                </Row>
            )}

            {mutation.extraDropoffLocations?.map((location, fieldIndex) => {
                const key = arrayFieldsIdsMap.extraDropoffLocations[fieldIndex]
                return (
                    <ConfirmShipmentDetailsWidgetEditableFieldWrapper
                        key={key}
                        onRemove={() =>
                            onRemoveArrayField({
                                id: 'extraDropoffLocations',
                                index: fieldIndex,
                            })
                        }
                    >
                        <AddressSearchInput
                            hasAddressBookSearch
                            customerId={me.customerId}
                            label={`Delivery location ${fieldIndex + 2}`}
                            placeholder="Search delivery"
                            addressBookId={location.addressBookId}
                            value={location.placeId ?? ''}
                            onChange={(address) =>
                                onChange({
                                    ...mutation,
                                    extraDropoffLocations: (
                                        mutation.extraDropoffLocations ?? []
                                    ).map((extraDropoff, dropoffIndex) => {
                                        return dropoffIndex === fieldIndex
                                            ? address
                                            : extraDropoff
                                    }),
                                })
                            }
                        />
                    </ConfirmShipmentDetailsWidgetEditableFieldWrapper>
                )
            })}
            {isMobile ? (
                <>
                    {resizableFields.budget}
                    {resizableFields.serviceType}
                    {visibleSimpleFieldsMap.numberOfShipments &&
                        resizableFields.numberOfShipments}
                    {resizableFields.equipment}
                    {visibleSimpleFieldsMap.accessorials &&
                        resizableFields.accessorials}
                    {visibleSimpleFieldsMap.referenceId &&
                        resizableFields.referenceId}
                    {visibleSimpleFieldsMap.isApprovalRequired &&
                        resizableFields.isApprovalRequired}
                    {visibleSimpleFieldsMap.isRushedOrder &&
                        resizableFields.isRushedOrder}
                </>
            ) : (
                <>
                    <Row gap="p1">
                        <Block
                            widthPct={
                                visibleSimpleFieldsMap.numberOfShipments
                                    ? 33.3
                                    : 50
                            }
                        >
                            {resizableFields.budget}
                        </Block>
                        <Block
                            widthPct={
                                visibleSimpleFieldsMap.numberOfShipments
                                    ? 33.3
                                    : 50
                            }
                        >
                            {resizableFields.serviceType}
                        </Block>
                        {visibleSimpleFieldsMap.numberOfShipments && (
                            <Block widthPct={33}>
                                {resizableFields.numberOfShipments}
                            </Block>
                        )}
                    </Row>
                    <Row gap="p1">
                        <Block
                            widthPct={
                                visibleSimpleFieldsMap.referenceId ? 50 : 100
                            }
                        >
                            {resizableFields.equipment}
                        </Block>
                        {visibleSimpleFieldsMap.referenceId && (
                            <Block widthPct={50}>
                                {resizableFields.referenceId}
                            </Block>
                        )}
                    </Row>
                    {visibleSimpleFieldsMap.accessorials &&
                        resizableFields.accessorials}
                </>
            )}

            {visibleSimpleFieldsMap.specialInstructions && (
                <ConfirmShipmentDetailsWidgetEditableFieldWrapper
                    onRemove={() => onRemoveSimpleField('specialInstructions')}
                >
                    <TextArea
                        label="Special instructions"
                        placeholder="(e.g., dimensions, unique commodities, appointment times, etc.)"
                        minRows={3}
                        maxRows={8}
                        value={mutation.specialInstructions ?? ''}
                        onChange={(specialInstructions) =>
                            onChange({
                                ...mutation,
                                specialInstructions,
                            })
                        }
                    />
                </ConfirmShipmentDetailsWidgetEditableFieldWrapper>
            )}
            {(!!mutation.commodities?.length || isLTL) && (
                <MeasurementsTypeSelect
                    label="Measurements"
                    weightType={mutation.weightType ?? 'lbs'}
                    measurementType={mutation.measurementType ?? 'in'}
                    onChange={(weightType, measurementType) =>
                        onChange({ ...mutation, weightType, measurementType })
                    }
                />
            )}
            {mutation.commodities?.map((commodity, fieldIndex) => {
                const commodityId = arrayFieldsIdsMap.commodities[fieldIndex]
                const commodityErrors = errors.commodities?.[fieldIndex] ?? {}

                return (
                    <ConfirmShipmentDetailsWidgetCommodity
                        isMobile={isMobile}
                        hideDeleteButton={
                            isLTL &&
                            !!mutation.commodities?.length &&
                            mutation.commodities.length === 1
                        }
                        isRequiredLabel={!!isLTL}
                        key={commodityId}
                        label={`Commodity ${fieldIndex + 1}`}
                        commodity={commodity}
                        commodityErrors={commodityErrors}
                        measurementType={mutation.measurementType ?? 'in'}
                        weightType={mutation.weightType ?? 'lbs'}
                        onDuplicate={() => {
                            onAddArrayField({
                                id: 'commodities',
                                object: commodity,
                            })
                        }}
                        onChange={(newCommodity) => {
                            onChange({
                                ...mutation,
                                commodities: mutation.commodities?.map(
                                    (oldCommodity, index) =>
                                        index === fieldIndex
                                            ? newCommodity
                                            : oldCommodity
                                ),
                            })
                        }}
                        onRemove={() =>
                            onRemoveArrayField({
                                id: 'commodities',
                                index: fieldIndex,
                            })
                        }
                    />
                )
            })}

            {visibleSimpleFieldsMap.isApprovalRequired &&
                resizableFields.isApprovalRequired}

            {visibleSimpleFieldsMap.isRushedOrder &&
                resizableFields.isRushedOrder}

            <StyledAdditionalFieldsOptionsToggle
                ref={extraFieldsOptionsRef}
                gap="p1"
                alignItems="center"
                onClick={() => setIsExtraFieldsOpen((prev) => !prev)}
            >
                <ExpandArrow isOpen={isExtraFieldsOpen} />
                <Typography isBold variant="body2" color="primary">
                    Add additional information
                </Typography>
            </StyledAdditionalFieldsOptionsToggle>
            {isExtraFieldsOpen && (
                <Row gap="p1" flexWrap="wrap" mb="p1">
                    {EXTRA_FIELDS_OPTIONS.filter((option) => {
                        if (
                            // If the field is a simple field and it's already visible
                            // we can remove it from the options.
                            typedArrayIncludes(
                                SIMPLE_EXTRA_FIELDS,
                                option.id
                            ) &&
                            visibleSimpleFieldsMap[option.id]
                        ) {
                            return false
                        }
                        // Otherwise, if non-visible simple field or array field we can render it.
                        // Array fields can be added unlimited amount of times.
                        return true
                    }).map((option) => (
                        <Button
                            key={option.id}
                            variant="secondary"
                            size="sm"
                            onClick={() => _onAddFieldOptionClick(option.id)}
                        >
                            <Plus size={16} />
                            {option.name}
                        </Button>
                    ))}
                </Row>
            )}

            <Row gap="p1" justifyContent="flex-end">
                <Button variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={onBook}>
                    Book
                </Button>
            </Row>
        </Column>
    )
}
